/**
 * Welches Abrechnungsmodell ein neuer Beleg tatsaechlich bekommt.
 *
 * WARUM diese Datei: Vorfall Projekt 6172. Dort wurde ein Rechnungsentwurf
 * KUMULATIV angelegt, obwohl das Projekt fortschreibend abrechnet. Kumulativ
 * nimmt den vollen Leistungsstand und zieht die bereits abgerechnete Menge
 * bewusst NICHT ab — der Abzug laeuft allein ueber die Vorbelege im System.
 * Vom Altsystem uebernommene Projekte haben diese Vorbelege nicht: der Entwurf
 * forderte 237.369,30 € statt 11.520 € netto, weil rund 188.000 € bereits im
 * Altsystem abgerechnete Leistung erneut gefordert wurden.
 *
 * ENTSCHEIDUNG des Betreibers (hart): Hat ein Projekt bereits ausgestellte
 * Rechnungen, wird ein neuer Beleg IMMER im Modell dieses Projekts angelegt.
 * Der Haken „Kumulativ" im Anlagedialog gilt dann nur noch fuer Projekte OHNE
 * Rechnungshistorie. Eine Warnung reichte nicht — sie stand bei 6172 da und hat
 * nichts verhindert.
 *
 * ZWEITER GRUND (Flottenbefund 18.08.): ueber 150 Projekte tragen im
 * Leistungsverzeichnis Altfakturierung (quantityBilled) OHNE einen einzigen
 * Beleg in OS:IA — 5666 Ueberseequartier 125.580 €, 6051 German LNG Terminal
 * 116.025 €, 5937 Gaskraftwerk Hamburg 82.080 €. Das ist kein Defekt: die
 * Abrechnungshistorie liegt im Altsystem, das Rechnungsmodul ist neu. Fuer
 * genau diese Projekte greift die Regel oben NICHT — sie haben keine
 * Rechnungshistorie, aus der sich ein Modell ablesen liesse, also entscheidet
 * weiter der Dialog-Haken. Und der merkt sich die letzte Wahl und vererbt
 * „kumulativ" von Projekt zu Projekt. Genau so entstand 6172.
 *
 * Deshalb: ein Projekt ohne Rechnungshistorie, das ungedeckte Altfakturierung
 * traegt, wird IMMER fortschreibend angelegt. Der Zwang loest sich von selbst
 * auf, sobald die Altrechnung erfasst ist — als externer Alt-Abschlag oder
 * ueber die Uebernahme der Alt-Positionen. Kein Schalter, den jemand
 * zurueckstellen muss.
 *
 * Bewusst pure Funktionen ohne React, DOM oder Supabase (stehende
 * Entscheidung „duenner Container, Logik in puren Funktionen"; das Projekt hat
 * keine DOM-Testumgebung). Sie liefern zusaetzlich, OB und WARUM die
 * Dialogwahl ueberstimmt wurde — die Anlageliste sagt damit an, wie der Beleg
 * entsteht, statt nur vor einer Abweichung zu warnen.
 */

// Nur die Typen: die Ableitung unten muss jede Belegart benennen, bleibt dafuer
// aber ohne Laufzeitabhaengigkeit (import type wird beim Uebersetzen entfernt).
import type { DocumentType, ExternalAdvance, ServiceItem, ServiceTransaction } from '../types';
import { splitGross, sumCents, type Cents } from './money';
import {
    computeCumulativePositions,
    uncoveredPriorBillingOf,
    type UncoveredPriorBilling,
} from './invoiceLogic';
import { altCoverageByService } from './externalAdvances';

/** Die beiden Abrechnungsmodelle, wie sie auch in `details.billingMode` stehen. */
export type BillingMode = 'CUMULATIVE' | 'PROGRESSIVE';

/** Der Haken im Anlagedialog als Modell gelesen. */
export const billingModeFromFlag = (cumulative: boolean): BillingMode =>
    cumulative ? 'CUMULATIVE' : 'PROGRESSIVE';

/** Deutsche Benennung fuer Hinweistexte. */
export const billingModeLabel = (mode: BillingMode): string =>
    mode === 'CUMULATIVE' ? 'kumulativ' : 'fortschreibend';

/**
 * Woraus das Modell folgt — in genau dieser Rangfolge geprueft.
 *
 * 'established'  Das Projekt hat ausgestellte Belege; ihr Modell gilt. Es
 *                schlaegt auch den Zwang: ein Projekt, das kumulativ abrechnet,
 *                rechnet weiter kumulativ ab, und die ungedeckte
 *                Altfakturierung wird beim Ausstellen zur Sperre
 *                (prior_billing_not_deducted). Ein Modellwechsel mitten in
 *                einer laufenden Abrechnung waere der groessere Schaden — er
 *                brachte den Abzug der Vorbelege durcheinander.
 * 'uncovered-prior-billing'
 *                Keine Historie, aber Altfakturierung ohne Belegdeckung.
 *                Fortschreibend erzwungen.
 * 'chosen'       Der Haken im Dialog gilt unveraendert.
 */
export type BillingModeReason = 'established' | 'uncovered-prior-billing' | 'chosen';

export interface EffectiveBillingMode {
    /** Das Modell, in dem der Beleg anzulegen ist. Massgeblich fuer ALLES. */
    mode: BillingMode;
    /**
     * Derselbe Wert als Haken-Bedeutung — fuer die Aufrufer, die frueher den
     * Dialogzustand `cumulative` gelesen haben. Genau dieser Wert gehoert an
     * jede dieser Stellen; eine vergessene erzeugt einen widerspruechlichen
     * Beleg (Positionen kumulativ gebaut, Abzug nicht angesetzt).
     */
    cumulative: boolean;
    /** Was der Nutzer im Dialog gewaehlt hat. */
    chosen: BillingMode;
    /** Das aus den ausgestellten Belegen abgelesene Projektmodell, sonst null. */
    established: BillingMode | null;
    /** Woraus `mode` folgt — siehe BillingModeReason. */
    reason: BillingModeReason;
    /** true, wenn die Dialogwahl ueberstimmt wurde (gleich aus welchem Grund). */
    overridden: boolean;
}

/**
 * Der effektive Modus. Rangfolge: etabliertes Projektmodell > ungedeckte
 * Altfakturierung > Dialog-Haken.
 *
 * @param chosenCumulative Haken „Kumulativ" im Anlagedialog.
 * @param established      Modell der bereits AUSGESTELLTEN Belege des Projekts
 *                         (siehe `priorBillingMode` in der Rechnungsansicht) —
 *                         `null`/`undefined`, wenn es keine Historie gibt.
 * @param hasUncoveredPriorBilling
 *                         Traegt das Projekt bereits abgerechnete Menge ohne
 *                         Belegdeckung? Ermittelt mit
 *                         projectUncoveredPriorBilling — DERSELBEN Rechnung,
 *                         die den fertigen Beleg beim Ausstellen sperrt.
 *                         Bewusst ein Boolescher Wert und kein Betrag: welchen
 *                         Modus der Beleg bekommt, haengt nur am OB. Der Betrag
 *                         gehoert in die Ansage, und die hat den Befund selbst.
 *                         `false` heisst hier ausdruecklich „kein Zwang" — auch
 *                         dann, wenn die Deckung gar nicht ermittelt werden
 *                         konnte. Ein Ladefehler darf keine Abrechnungsart
 *                         erzwingen; der Beleg laeuft dann in die Sperre beim
 *                         Ausstellen, die dieselbe Frage stellt.
 *
 * Ohne Historie und ohne ungedeckte Altfakturierung gilt die Wahl unveraendert:
 * ein neues Projekt darf frei festgelegt werden, und genau dieser erste Beleg
 * begruendet das Modell.
 */
export const resolveBillingMode = (
    chosenCumulative: boolean,
    established: BillingMode | null | undefined,
    hasUncoveredPriorBilling = false,
): EffectiveBillingMode => {
    const chosen = billingModeFromFlag(chosenCumulative);
    let mode: BillingMode;
    let reason: BillingModeReason;
    if (established != null) {
        mode = established;
        reason = 'established';
    } else if (hasUncoveredPriorBilling && chosen === 'CUMULATIVE') {
        mode = 'PROGRESSIVE';
        reason = 'uncovered-prior-billing';
    } else {
        // Auch mit ungedeckter Altfakturierung: wer ohnehin fortschreibend
        // waehlt, wird nicht ueberstimmt — es gibt nichts zu ueberstimmen.
        mode = chosen;
        reason = 'chosen';
    }
    return {
        mode,
        cumulative: mode === 'CUMULATIVE',
        chosen,
        established: established ?? null,
        reason,
        // Nur echtes Ueberstimmen: gleiches Modell ist keine Abweichung und
        // darf in der Liste auch nicht als solche angesagt werden.
        overridden: mode !== chosen,
    };
};

// ─── Ungedeckte Altfakturierung am PROJEKTSTAND ─────────────────────────────

/** Was der Anlagedialog ueber ein Projekt weiss, wenn er die Deckung prueft. */
export interface ProjectPriorBilling {
    /** Leistungsverzeichnis des Projekts (getNormalizedServices). */
    services: readonly ServiceItem[];
    /** Leistungsprotokoll (data.metadata.globalData.serviceLog). */
    serviceLog: readonly ServiceTransaction[];
    /**
     * Menge je Leistung, die die abzugsfaehigen Vorbelege des Projekts
     * beansprucht haben — advanceInvoiceLogic.invoicedQuantityByService.
     *
     * Hereingereicht und nicht hier gerechnet: invoiceLogic ist die
     * Basisschicht, advanceInvoiceLogic liest daraus. Ein Import von dort
     * schloesse einen Zyklus.
     */
    invoicedQtyByService: ReadonlyMap<string, number>;
    /**
     * Externe Alt-Abschlaege des Projekts (public.external_advances).
     *
     * `null` heisst „nicht ermittelt" und ist der Grund, warum dieser Parameter
     * ueberhaupt nullable ist: OHNE sie laesst sich die Frage nicht beantworten,
     * denn genau ihr Erfassen ist der zugesagte Weg, den Zwang aufzuloesen.
     * Ein Ladefehler darf aber keine Abrechnungsart erzwingen — deshalb kein
     * Befund statt eines Befunds auf unvollstaendiger Grundlage.
     */
    externalAdvances: readonly ExternalAdvance[] | null;
    /** Stichtag des Belegs (yyyy-mm-dd) — derselbe wie im Anlagedialog. */
    asOf?: string;
    /** Mengen ohne Protokolleintrag mitzaehlen — wie im Anlagedialog. */
    includeUndated?: boolean;
}

/**
 * Ungedeckte Altfakturierung, am PROJEKTSTAND statt am fertigen Beleg
 * gerechnet — die Vorwegnahme von uncoveredPriorBilling.
 *
 * Sie baut GENAU die Positionen nach, die der kumulative Beleg bekaeme:
 * dieselbe Auswahl (computeCumulativePositions mit demselben Stichtag),
 * dieselben beiden Deckungsquellen (Vorbelege des Projekts und ALT-
 * Protokollzeilen — buildProjectServiceLines addiert eben diese zu
 * previouslyInvoicedQty), derselbe Einzelpreis (roundCents(unitPrice x 100),
 * ebenfalls aus computeCumulativePositions). Bewertet wird dann mit
 * uncoveredPriorBillingOf, also mit derselben Funktion, die beim Ausstellen
 * sperrt.
 *
 * NAEHERUNGEN gegenueber dem fertigen Beleg, beide bewusst:
 *
 * 1. Zeilennachlass. Am Projektstand gibt es keinen — er wird erst im Editor
 *    gesetzt. Ein Nachlass mindert beide Seiten gleich (billedValue und
 *    Luecke), verschiebt also nur den Betrag, nie das OB. Der Sockel von
 *    1,00 EUR ist der einzige Punkt, an dem er ueberhaupt wirken koennte, und
 *    dort nur bei einer Luecke knapp ueber dem Sockel.
 * 2. Bestandsalter. Die Vorbelege stammen aus dem Bestand des Anlagedialogs,
 *    der fertige Beleg holt sie frisch. Der Dialog laedt seinen Bestand beim
 *    Oeffnen und verwirft ihn bei jedem Neuladen der Liste; er kann also
 *    hoechstens einen Beleg nicht kennen, der in derselben Sitzung nebenher
 *    ausgestellt wurde.
 *
 * Keine der beiden beruehrt die Zusage: der externe Alt-Abschlag geht hier
 * ueber DIESELBE Betragsrechnung ein wie beim Beleg (Netto aus Brutto ueber
 * splitGross, identisch mit deductionsFromExternalAdvances). Wer die
 * Altrechnung erfasst, senkt die Luecke hier um genau denselben Betrag wie
 * dort — und der Zwang faellt weg, sobald sie unter den Sockel faellt.
 *
 * @returns Der Befund, oder null (kein Zwang) — auch wenn die Alt-Abschlaege
 *          nicht ermittelt werden konnten.
 */
export const projectUncoveredPriorBilling = (
    input: ProjectPriorBilling,
): UncoveredPriorBilling | null => {
    // Nicht ermittelt heisst nicht „keine": ohne die Alt-Abschlaege waere jedes
    // Bestandsprojekt mit erfasster Altrechnung faelschlich im Zwang, und der
    // Betreiber haette keinen Weg mehr heraus.
    if (input.externalAdvances == null) return null;

    const positions = computeCumulativePositions([...input.services], {
        to: input.asOf,
        serviceLog: [...input.serviceLog],
        includeUndated: input.includeUndated,
    });
    if (positions.length === 0) return null;

    const alt = altCoverageByService([...input.serviceLog]);
    // Netto aus Brutto ueber splitGross — Zeile fuer Zeile wie in
    // deductionsFromExternalAdvances. Erst je Beleg runden, dann summieren:
    // eine Summe der Bruttobetraege mit einer einzigen Aufteilung ergaebe bei
    // gemischten Steuersaetzen einen anderen Cent als der Beleg selbst.
    const externalCoverCents: Cents = sumCents(
        input.externalAdvances.map((row) => splitGross(row.grossCents, row.taxRateBp).netCents),
    );

    return uncoveredPriorBillingOf(
        positions.map((pos) => ({
            lvPosition: pos.service.lvPosition,
            title: pos.service.title,
            unit: pos.service.unit,
            billedQty: pos.service.quantityBilled || 0,
            coveredQty:
                (input.invoicedQtyByService.get(pos.service.id) ?? 0) +
                (alt.get(pos.service.id)?.quantity ?? 0),
            unitPriceCents: pos.unitPriceCents,
        })),
        externalCoverCents,
    );
};

// ─── Welcher Beleg ueberhaupt eine Aussage ueber das Modell trifft ──────────

/**
 * Legt der Erzeugungspfad dieser Belegart ein Abrechnungsmodell fest?
 *
 * Das Modell steht in `details.billingMode`, und dort steht es NUR bei
 * kumulativen Belegen (invoiceService.ts:2682 — `...(input.cumulative ?
 * { billingMode: 'CUMULATIVE' } : {})`). Fortschreibend ist also das FEHLEN
 * des Feldes. Genau daran haengt die Falle: bei Belegarten, deren Pfad das Feld
 * ueberhaupt nie setzt, bedeutet das Fehlen nicht "fortschreibend", sondern
 * "keine Angabe". Wer beides gleich liest, macht aus einer Gutschrift ueber
 * 500 € die Aussage, das Projekt rechne ab sofort fortschreibend ab.
 *
 * Am Code geprueft (invoiceService.ts, creditNoteLogic.ts):
 * - INVOICE / FINAL_INVOICE / REMAINING_INVOICE — createFinalInvoice setzt das
 *   Modell (invoiceService.ts:2668/2682); die Wege ohne Abschlagsabzug
 *   (createInvoiceDraft in InvoicesView.tsx:1529, billingRunService.ts:352)
 *   erzeugen bewusst fortschreibende Belege. Aussagekraeftig.
 * - ADVANCE_INVOICE — createAdvanceInvoice (invoiceService.ts:2429) uebergibt
 *   keine details. Ein Abschlag ist eine Zahlungsanforderung auf den
 *   Gesamtauftrag und sagt ueber das Modell nichts.
 * - CORRECTION_INVOICE — correctInvoice (invoiceService.ts:3297) laesst das
 *   Modell ausdruecklich weg; die Korrektur traegt Differenzen.
 * - COMMERCIAL_CREDIT_INVOICE — createCommercialCredit (invoiceService.ts:3373)
 *   hat keinen billingMode im details-Block.
 * - SELF_BILLING_CREDIT_NOTE — createSelfBillingCreditNote
 *   (invoiceService.ts:3486) setzt nur reasonText und headerText.
 * - CANCELLATION_INVOICE — cancelInvoice (invoiceService.ts:3172) uebernimmt
 *   das Modell des Ursprungsbelegs, WENN dieser eines hat. Das Feld gehoert
 *   dort hin und muss bleiben: der Storno spiegelt die Zeilen des Originals,
 *   und Ausstellpruefung (invoiceService.ts:2097), Ablosesperre (2287),
 *   Zuordnung und Rueckschreibung (1656/1676) lesen es an DIESEM Beleg.
 *   Ueber das PROJEKT sagt es trotzdem nichts: der geerbte Wert beschreibt den
 *   aufgehobenen Beleg, nicht den Stand nach der Aufhebung. Deshalb `false`,
 *   und deshalb wird die Tabelle unten VOR dem Feld geprueft — sonst waere
 *   das Storno eines alten kumulativen Belegs die juengste Aussage des
 *   Projekts und kippte es zurueck auf kumulativ, obwohl inzwischen
 *   fortschreibend abgerechnet wird.
 *
 * Bewusst ein `Record` ueber die vollstaendige Belegart und keine Menge: eine
 * neue Belegart bricht dann den Typecheck, statt stillschweigend als
 * "fortschreibend" zu gelten.
 */
const STATES_BILLING_MODE: Record<DocumentType, boolean> = {
    INVOICE: true,
    FINAL_INVOICE: true,
    REMAINING_INVOICE: true,
    ADVANCE_INVOICE: false,
    CANCELLATION_INVOICE: false,
    CORRECTION_INVOICE: false,
    COMMERCIAL_CREDIT_INVOICE: false,
    SELF_BILLING_CREDIT_NOTE: false,
};

/** Das Wenige, was ein Beleg fuer diese Ableitung mitbringen muss. */
export interface BillingModeStatement {
    /** Erst ab dem Ausstellen gesetzt — ein Entwurf ist nirgends. */
    invoiceNumber?: string | null;
    documentType?: DocumentType | null;
    invoiceDate?: string | null;
    details?: { billingMode?: 'CUMULATIVE' } | null;
}

export interface ProjectBillingModeStatement extends BillingModeStatement {
    primaryProjectId?: string | null;
}

/**
 * Wie DIESER eine Beleg gerechnet ist — `null` heisst "keine Abrechnungsart",
 * nicht "fortschreibend".
 *
 * Fuer die ANZEIGE am Beleg gedacht (Belegliste, Belegkopf): sie beantwortet
 * die Frage „wie ist dieses Dokument gerechnet", und die stellt sich auch am
 * Entwurf — dessen Positionen stehen bereits kumulativ oder eben nicht.
 * Deshalb ohne die Entwurfsschranke von `statedBillingMode`, die eine andere
 * Frage stellt (siehe dort).
 *
 * Ansonsten dieselbe Ableitung und dieselbe Tabelle: ERST die Belegart, dann
 * das Feld. Wer nur `details.billingMode` liest, macht aus jeder Gutschrift und
 * jedem Abschlag die Aussage "fortschreibend", weil fortschreibend das FEHLEN
 * des Feldes ist.
 */
export const documentBillingMode = (inv: BillingModeStatement): BillingMode | null => {
    const type = inv.documentType;
    if (!type || STATES_BILLING_MODE[type] !== true) return null;
    // Innerhalb der aussagekraeftigen Arten steht das Modell im Feld: gesetzt
    // heisst kumulativ, fehlend fortschreibend (nur der kumulative Pfad setzt
    // es, invoiceService.ts:2753).
    return inv.details?.billingMode === 'CUMULATIVE' ? 'CUMULATIVE' : 'PROGRESSIVE';
};

/**
 * Was DIESER eine Beleg ueber das Abrechnungsmodell DES PROJEKTS aussagt —
 * `null` heisst "keine Aussage" und darf ein bestehendes Modell weder setzen
 * noch ueberschreiben.
 */
export const statedBillingMode = (inv: BillingModeStatement): BillingMode | null => {
    // Entwuerfe zaehlen nicht: erst ein ausgestellter Beleg ist beim Kunden.
    // Genau hier gehen die beiden Fragen auseinander — der Entwurf IST bereits
    // kumulativ gerechnet (documentBillingMode sagt das auch), begruendet aber
    // noch kein Projektmodell, an das die naechste Rechnung anschliessen muss.
    if (!inv.invoiceNumber) return null;
    // ERST die Belegart, dann das Feld — nie umgekehrt. Frueher schlug ein
    // ausdrueckliches `billingMode` die Tabelle, damit das Storno eines
    // kumulativen Belegs dessen Modell weitertraegt. Genau das war der Fehler:
    // Januar kumulativ, Februar fortschreibend, im Maerz das Storno der
    // Januarrechnung — das Storno erbt 'CUMULATIVE' und ist mit seinem Datum
    // die juengste Aussage. Das Projekt galt danach wieder als kumulativ, und
    // weil die Ableitung bindend ist, entstanden Folgebelege kumulativ. Der
    // geerbte Wert beschreibt den AUFGEHOBENEN Beleg, nicht den Stand danach.
    return documentBillingMode(inv);
};

/**
 * Ordnung der Belege untereinander: Datum zuerst, Nummer als Gleichstand —
 * zwei Belege koennen denselben Tag tragen, und die Nummern laufen fortlaufend.
 */
const stampOf = (inv: BillingModeStatement): string =>
    `${inv.invoiceDate ?? ''}|${inv.invoiceNumber ?? ''}`;

/**
 * Das Modell der bereits ausgestellten Belege eines Projekts.
 *
 * Massgeblich ist der juengste AUSSAGEKRAEFTIGE Beleg: an den muss die
 * naechste Rechnung anschliessen. Belege ohne Aussage werden uebersprungen,
 * nicht als "fortschreibend" gelesen. `null` heisst "keine Aussage" — dann
 * entscheidet die Wahl im Dialog beziehungsweise die Voreinstellung.
 */
export const establishedBillingMode = (
    invoices: readonly BillingModeStatement[],
): BillingMode | null => {
    let newest = '';
    let mode: BillingMode | null = null;
    for (const inv of invoices) {
        const stated = statedBillingMode(inv);
        if (stated == null) continue;
        const stamp = stampOf(inv);
        if (stamp < newest) continue;
        newest = stamp;
        mode = stated;
    }
    return mode;
};

/**
 * Dasselbe ueber eine Belegliste mehrerer Projekte, nach `primaryProjectId`
 * getrennt. Fehlt der Projektbezug (Sammelrechnung), zaehlt der Beleg fuer
 * kein Projekt — die Zuordnung stuende dort in den Zeilen.
 */
export const establishedBillingModeByProject = (
    invoices: readonly ProjectBillingModeStatement[],
): Map<string, BillingMode> => {
    const model = new Map<string, BillingMode>();
    const newest = new Map<string, string>();
    for (const inv of invoices) {
        const projectId = inv.primaryProjectId;
        if (!projectId) continue;
        const stated = statedBillingMode(inv);
        if (stated == null) continue;
        const stamp = stampOf(inv);
        if ((newest.get(projectId) ?? '') > stamp) continue;
        newest.set(projectId, stamp);
        model.set(projectId, stated);
    }
    return model;
};

// ─── Die drei Zustaende eines Projekts im Anlagedialog ──────────────────────

/**
 * WARUM dieser Abschnitt: der Haken „Kumulativ abrechnen" stand im
 * Anlagedialog in der LINKEN Spalte — also bevor ueberhaupt ein Projekt
 * gewaehlt war. Bei jedem Projekt mit Abrechnungshistorie war er funktionslos,
 * weil resolveBillingMode ihn ohnehin ueberstimmt; gefragt wurde also
 * ausgerechnet dort am lautesten, wo es nichts zu entscheiden gab. Gefragt
 * wird jetzt nur noch dort, wo die Frage eine Antwort haben KANN: beim ersten
 * Beleg eines Projekts.
 *
 * Die drei Zustaende sind dieselbe Rangfolge wie in BillingModeReason, nur als
 * Frage an das PROJEKT statt an die Wahl:
 *
 *  'established'        Das Projekt hat ausgestellte Belege mit Aussage; ihr
 *                       Modell gilt. Nur anzeigen, nicht fragen.
 *  'forced-progressive' Keine Aussage, aber Altfakturierung ohne Belegdeckung.
 *                       Fortschreibend — und zwar VORLAEUFIG: sobald die
 *                       Altrechnung als Alt-Abschlag erfasst (oder die
 *                       Alt-Position uebernommen) ist, faellt der Grund weg und
 *                       dasselbe Projekt steht auf 'open'. Genau das ist der
 *                       Weg fuer die rund 150 Bestandsprojekte, die kumulativ
 *                       werden sollen; der Text an der Zeile muss es sagen.
 *  'checking'           Die Deckungspruefung LAEUFT noch. Weder noch — aber
 *                       noch nicht entschieden, und das ist etwas anderes als
 *                       'open'. Der Unterschied ist nicht kosmetisch: die
 *                       Projektliste haengt am Belegbestand, nicht an den
 *                       Alt-Abschlaegen, und erscheint deshalb, bevor deren
 *                       Abfrage geantwortet hat. In genau diesem Fenster saehe
 *                       ein Bestandsprojekt mit ungedeckter Altfakturierung wie
 *                       ein frisches aus, und „kumulativ" waere waehlbar — die
 *                       Bauart von Vorfall 6172. Ein FEHLGESCHLAGENER Ladelauf
 *                       ist etwas anderes und faellt weiter auf 'open' zurueck:
 *                       ein Fehler darf keine Abrechnungsart erzwingen, und die
 *                       Sperre beim Ausstellen stellt dieselbe Frage noch einmal.
 *  'open'               Weder noch — die Frage wird gestellt, und dieser erste
 *                       Beleg begruendet das Modell.
 *
 * FALLE, die diese Ableitung ausschliesst: 'established' darf NIE ueber die
 * Belegzahl eines Projekts bestimmt werden (tallyProjectDocuments → `issued`).
 * Die zaehlt JEDE Belegart mit Nummer; ein Projekt mit ausschliesslich
 * ausgestellten Abschlagsrechnungen haette dort `issued > 0`, obwohl der
 * Abschlag ueber das Modell nichts aussagt (STATES_BILLING_MODE:
 * ADVANCE_INVOICE = false). Der Dialog unterdrueckte dann die Frage und zeigte
 * ein Modell an, das es gar nicht gibt. Massgeblich ist ausschliesslich
 * establishedBillingMode / establishedBillingModeByProject.
 */
export type BillingStanceKind =
    | 'established'
    | 'forced-progressive'
    | 'checking'
    | 'open';

/**
 * Bewusst eine unterscheidbare Vereinigung und kein Interface mit lauter
 * nullbaren Feldern: nur so weiss der Aufrufer im Zweig 'established', dass
 * `fixedMode` gesetzt ist, und im Zweig 'forced-progressive', dass der Befund
 * vorliegt. Sonst stuenden an jeder Textstelle Ausrufezeichen — und genau dort
 * steht der Betrag, den der Betreiber liest.
 *
 * `fixedMode` ist `null` AUSSCHLIESSLICH bei 'open'; `uncovered` traegt den
 * Betrag, ohne den „fortschreibend gesperrt" eine Behauptung ohne Beleg waere.
 */
export type BillingStance =
    | { kind: 'established'; fixedMode: BillingMode; uncovered: null }
    | { kind: 'forced-progressive'; fixedMode: 'PROGRESSIVE'; uncovered: UncoveredPriorBilling }
    | { kind: 'checking'; fixedMode: null; uncovered: null }
    | { kind: 'open'; fixedMode: null; uncovered: null };

/** Der Zustand ohne jede Vorgeschichte — auch der Rueckfall fuer unbekannte Projekte. */
export const OPEN_BILLING_STANCE: BillingStance = {
    kind: 'open',
    fixedMode: null,
    uncovered: null,
};

/** Solange die Deckungspruefung laeuft — noch nichts entschieden. */
export const CHECKING_BILLING_STANCE: BillingStance = {
    kind: 'checking',
    fixedMode: null,
    uncovered: null,
};

/**
 * Der Zustand eines Projekts aus denselben zwei Befunden, aus denen auch
 * resolveBillingMode entscheidet — und in derselben Rangfolge.
 *
 * @param established Modell der ausgestellten Belege, `null` ohne Aussage.
 * @param uncovered   Befund von projectUncoveredPriorBilling, `null` heisst
 *                    „kein Zwang" (auch dann, wenn die Alt-Abschlaege gar nicht
 *                    ermittelt werden konnten — ein Ladefehler darf keine
 *                    Abrechnungsart erzwingen).
 * @param coverageKnown
 *                    Ist die Deckungspruefung ABGESCHLOSSEN? `false` heisst
 *                    „laeuft noch" und ergibt 'checking'. Nicht zu verwechseln
 *                    mit dem Ladefehler: der ist abgeschlossen, nur ergebnislos,
 *                    und faellt bewusst auf 'open' zurueck. Voreinstellung
 *                    `true`, weil jeder Aufrufer ohne eigene Ladeverwaltung
 *                    (Tests, Rechnungslauf) mit fertigen Daten arbeitet.
 */
export const billingStanceOf = (
    established: BillingMode | null | undefined,
    uncovered: UncoveredPriorBilling | null | undefined,
    coverageKnown = true,
): BillingStance => {
    if (established != null) {
        return { kind: 'established', fixedMode: established, uncovered: null };
    }
    if (uncovered != null) {
        return { kind: 'forced-progressive', fixedMode: 'PROGRESSIVE', uncovered };
    }
    // Reihenfolge: erst die beiden ENTSCHIEDENEN Zustaende. Ein Projekt mit
    // etabliertem Modell oder bereits gefundener Luecke braucht die laufende
    // Abfrage nicht abzuwarten — an beidem aendert sie nichts.
    if (!coverageKnown) return CHECKING_BILLING_STANCE;
    return OPEN_BILLING_STANCE;
};

/**
 * Das Modell, in dem der Beleg wirklich entsteht.
 *
 * `chosen` ist die Antwort auf die Frage im Dialog und gilt AUSSCHLIESSLICH bei
 * 'open'. Bei den beiden anderen Zustaenden gibt es keine Frage, also auch
 * nichts zu ueberstimmen — deshalb hier kein `overridden` mehr: ueberstimmt
 * werden konnte nur ein Haken, den es nicht mehr gibt.
 */
export const modeFromStance = (stance: BillingStance, chosen: BillingMode): BillingMode =>
    stance.fixedMode ?? chosen;

/**
 * Die Zustaende aller Projekte einer Liste, aus den beiden ohnehin vorhandenen
 * Karten.
 *
 * WARUM als eigene Funktion: der Anlagedialog braucht denselben Zustand an drei
 * Stellen (Anzeige an der Zeile, Rueckfrage nach dem Klick, Anlegen des
 * Belegs). Er wurde dort dreimal einzeln neu abgeleitet, einmal davon in der
 * Zeilenschleife — bei zweihundert Projekten zweihundertmal je Durchlauf. Drei
 * Ableitungen desselben Sachverhalts laufen frueher oder spaeter auseinander,
 * und die Stelle, an der sie es tun, entscheidet ueber den Belegbetrag.
 */
export const billingStancesByProject = (
    projectIds: readonly string[],
    established: ReadonlyMap<string, BillingMode>,
    uncovered: ReadonlyMap<string, UncoveredPriorBilling>,
    coverageKnown = true,
): Map<string, BillingStance> => {
    const out = new Map<string, BillingStance>();
    for (const id of projectIds) {
        out.set(id, billingStanceOf(established.get(id), uncovered.get(id), coverageKnown));
    }
    return out;
};

/**
 * Liest der Erzeugungspfad DIESER Belegart das Abrechnungsmodell ueberhaupt?
 *
 * Bewusst eine zweite Tabelle neben STATES_BILLING_MODE, obwohl beide heute
 * dieselben Werte tragen: die eine beantwortet „sagt dieser Beleg etwas ueber
 * das Modell des Projekts aus" (Ableitung aus der Historie), diese hier „braucht
 * dieser Beleg beim Anlegen ein Modell". Das sind zwei Fragen, und sie zu einer
 * zusammenzuziehen hiesse, die naechste Belegart automatisch beiden Antworten
 * zu unterwerfen.
 *
 * Am Code geprueft: createFinalInvoice bekommt `cumulative` bei INVOICE,
 * FINAL_INVOICE und REMAINING_INVOICE, und buildProjectServiceLines baut die
 * Positionen danach. createAdvanceInvoice kennt den Parameter nicht — ein
 * Abschlag rechnet ueber den Auftragswert. Die uebrigen Belegarten entstehen
 * nicht aus einem Projekt, sondern aus einem Beleg.
 */
const CONSUMES_BILLING_MODE: Record<DocumentType, boolean> = {
    INVOICE: true,
    FINAL_INVOICE: true,
    REMAINING_INVOICE: true,
    ADVANCE_INVOICE: false,
    CANCELLATION_INVOICE: false,
    CORRECTION_INVOICE: false,
    COMMERCIAL_CREDIT_INVOICE: false,
    SELF_BILLING_CREDIT_NOTE: false,
};

export const consumesBillingMode = (type: DocumentType | null | undefined): boolean =>
    type != null && CONSUMES_BILLING_MODE[type] === true;

/**
 * Muss der Dialog vor dem Anlegen nach der Abrechnungsart fragen?
 *
 * Nur wenn beides zutrifft: das Projekt hat noch kein Modell UND die Belegart
 * liest ueberhaupt eines. Fuer den Abschlag waere die Frage ohne Wirkung — und
 * eine Frage ohne Wirkung ist genau das, was hier abgeschafft wurde.
 */
export const asksBillingMode = (
    stance: BillingStance,
    docType: DocumentType | null | undefined,
): boolean => stance.kind === 'open' && consumesBillingMode(docType);

/**
 * Muss der Klick auf dieses Projekt WARTEN?
 *
 * Nur solange die Deckungspruefung laeuft und die Belegart das Modell
 * ueberhaupt liest. Die Frage jetzt zu stellen hiesse, „kumulativ" anzubieten,
 * ohne zu wissen, ob es fuer dieses Projekt gesperrt ist — und die Antwort
 * waere nicht mehr einzufangen, sobald der Beleg entstanden ist. Ein paar
 * Zehntelsekunden warten ist der billigere Preis.
 */
export const awaitsCoverageCheck = (
    stance: BillingStance,
    docType: DocumentType | null | undefined,
): boolean => stance.kind === 'checking' && consumesBillingMode(docType);
